import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconRun from '@apollo/icons/default/IconRun.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import {
  Button,
  ButtonGroup,
  CloseButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { Fragment, useCallback } from 'react';

import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import { ScriptModalSnippets } from 'src/app/graph/explorerPage/ScriptModalSnippets';
import { useScriptModalState } from 'src/app/graph/explorerPage/useScriptModalState';
import { ResizerStyles } from 'src/components/common/resizerStyles/ResizerStyles';
import { ThreePaneLayout } from 'src/components/common/ThreePaneLayout';
import { StudioEditorContainer } from 'src/hooks/useStudioEditor';

import { GraphRef } from '../../hooks/useGraphRef';
import { EnvironmentVariablesEditor } from '../environmentVariablesEditor/EnvironmentVariablesEditor';
import { PreflightScriptOAuthConfirmationModal } from '../explorerSettings/PreflightScriptOAuthConfirmationModal';
import { ScriptPromptModal } from '../explorerSettings/ScriptPromptModal';
import { ExplorerDocsTab } from '../helpers/ExplorerDocsTab';
import { EditorTabState } from '../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { updateAllEnvironmentVariableEditors } from '../hooks/useExplorerState/useEnvironmentVariables';

export const ScriptEditorModal = ({
  onClose,
  graphRef,
  editorTabState,
  scriptEditor,
  endpointUrl,
  includeCookies,
  explorerBehavioralFlags,
  isOperationScriptEnabled,
  setDocsTab,
  graphqlSchema,
  scriptType,
  mockedResponseEditor,
}: {
  onClose: () => void;
  graphRef: GraphRef | null;
  editorTabState: EditorTabState;
  scriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  endpointUrl: string | null;
  includeCookies: boolean;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  isOperationScriptEnabled: boolean;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  graphqlSchema: GraphQLSchema;
  scriptType: 'preflightOperation' | 'postflightOperation';
  mockedResponseEditor: monacoEditor.editor.IStandaloneCodeEditor;
}) => {
  const {
    selectedTab,
    setPreflightOperationScriptByTab,
    setPostflightOperationScriptByTab,
    savedOperationLocalState,
    setMockedResponseByTab,
  } = editorTabState;

  const {
    logRef,
    layoutInfo,
    logs,
    environmentVariablesEditor,
    testRunScript,
    scriptRunning,
    oauth2Request,
    promptRequest,
    model,
    handleClose,
  } = useScriptModalState({
    graphRef,
    editor: scriptEditor,
    initialScriptValue: scriptEditor.getValue(),
    onClose,
    endpointUrl,
    includeCookies,
    explorerBehavioralFlags,
    currentOperationId:
      selectedTab && savedOperationLocalState[selectedTab]?.collectionEntryId,
    isOperationScriptEnabled,
    setDocsTab,
    graphqlSchema,
  });

  const handleSave = useCallback(() => {
    if (model) {
      const newScriptText = model.getValue();
      if (scriptType === 'postflightOperation') {
        setPostflightOperationScriptByTab(selectedTab ?? '', newScriptText);
        const mockedResponseModel = mockedResponseEditor.getModel();
        if (mockedResponseModel) {
          setMockedResponseByTab(
            selectedTab ?? '',
            mockedResponseModel.getValue(),
          );
        }
      } else {
        setPreflightOperationScriptByTab(selectedTab ?? '', newScriptText);
      }
    }
    updateAllEnvironmentVariableEditors();
    onClose();
  }, [
    model,
    onClose,
    selectedTab,
    setPreflightOperationScriptByTab,
    setPostflightOperationScriptByTab,
    scriptType,
    mockedResponseEditor,
    setMockedResponseByTab,
  ]);

  return (
    <>
      <Modal
        isOpen={true}
        onClose={handleClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent className="h-[80vh] md:mx-20">
          <ModalHeader className="border-b border-b-silver-dark">
            <ModalTitle>
              {scriptType === 'postflightOperation'
                ? 'Set up a Post-Operation script'
                : 'Set up a Pre-Operation script'}
            </ModalTitle>
            <ModalDescription>
              {scriptType === 'postflightOperation'
                ? 'This script will run after your operation.'
                : 'This script will run after your preflight script and before your operation.'}
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="-my-2 p-0">
            <ResizerStyles
              additionalCss={`
        .xxx-script-pane-layout .Resizer {
          margin-left: -6px;
          margin-right: -6px;
          z-index: 1;
        }
        .xxx-script-pane-layout .Resizer:before {
          display: none;
        }
      `}
            />
            <ThreePaneLayout
              layoutInfo={layoutInfo}
              className="xxx-script-pane-layout"
              leftPanel={
                <div className="flex h-full flex-col border-r border-primary">
                  <div className="ml-8 mr-2 mt-4 flex flex-none items-center font-semibold">
                    <div className="flex flex-1 items-center">
                      <IconCode className="mr-2" /> Snippets
                    </div>
                    <CloseButton
                      size="sm"
                      onClick={() => layoutInfo.setLeftPaneOpen(false)}
                    />
                  </div>
                  <div className="flex-1 overflow-auto pb-4">
                    <div className="ml-8 mr-6 flex-none py-2 text-sm text-secondary">
                      Use these snippets to set and access environment
                      variables, fetch operations, send network requests, and
                      prompt for user input.
                    </div>
                    <ScriptModalSnippets
                      scriptEditor={scriptEditor}
                      model={model}
                      isPostflight={scriptType === 'postflightOperation'}
                    />
                  </div>
                </div>
              }
              centerPanel={
                <div className="relative flex size-full flex-1 flex-col overflow-hidden border-r border-primary">
                  <div
                    className={classnames(
                      'flex h-10 flex-none items-center justify-between border-b border-primary pr-6',
                      layoutInfo.leftPaneOpen ? 'pl-4' : 'pl-9',
                    )}
                  >
                    <div className="text-sm font-semibold">Script editor</div>
                    <Button
                      variant="hidden"
                      onClick={() =>
                        testRunScript(
                          scriptType === 'postflightOperation'
                            ? jsoncParser.parse(mockedResponseEditor.getValue())
                            : '',
                        )
                      }
                      type="button"
                      leftIcon={
                        scriptRunning ? (
                          <Spinner
                            size="tiny"
                            className="w-2 -translate-x-px scale-150"
                            variant="neutral"
                          />
                        ) : (
                          <IconRun />
                        )
                      }
                      size="sm"
                    >
                      Test script
                    </Button>
                  </div>
                  <div
                    className="relative size-full bg-silver-lighter"
                    data-testid="operation_script_editor"
                  >
                    <StudioEditorContainer
                      editor={scriptEditor}
                      className={classnames(
                        // Exclude scripts from fullstory
                        'fs-exclude absolute size-full flex-1 overflow-hidden pr-4',
                        layoutInfo.leftPaneOpen ? '' : 'pl-2',
                      )}
                    />
                  </div>
                </div>
              }
              rightPanel={
                <div className="flex size-full flex-1 flex-col">
                  <div className="flex h-10 flex-none items-center border-b border-primary pl-4 pr-9 text-sm font-semibold">
                    Console output
                  </div>
                  <div
                    className="flex-1 overflow-auto bg-silver-lighter py-2 pl-2 pr-9 font-mono text-xs"
                    ref={logRef}
                  >
                    {logs
                      .slice()
                      .reverse()
                      .map((runLogs, runIndex) => {
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <Fragment key={runIndex}>
                            {runIndex > 0 && (
                              <div className="my-1 ml-4 border-b border-dashed" />
                            )}
                            {runLogs.map(([level, ...msgs], logIndex) => {
                              return (
                                <span
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`${runIndex}${logIndex}`}
                                  className={classnames({
                                    'text-blilet-dark': level === 'log',
                                    'text-yellow-dark': level === 'warn',
                                    'text-red-dark': level === 'error',
                                  })}
                                >
                                  {level === 'error' ? (
                                    <IconErrorSolid />
                                  ) : level === 'warn' ? (
                                    <IconWarningSolid />
                                  ) : (
                                    <div className="inline-block size-2" />
                                  )}{' '}
                                  {_.capitalize(level)}: {msgs.join(' ')}
                                  <br />
                                </span>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                  </div>
                  <div className="flex h-10 flex-none items-center border-y border-primary pl-4 pr-9 text-sm font-semibold">
                    Environment Variables
                  </div>
                  <EnvironmentVariablesEditor
                    editor={environmentVariablesEditor}
                    className="flex-1 bg-silver-lighter"
                  />
                  {scriptType === 'postflightOperation' && (
                    <div className="flex size-full flex-1 flex-col">
                      <div className="flex h-10 flex-none items-center border-y border-primary pl-4 pr-9 text-sm font-semibold">
                        Mocked Operation Response
                      </div>
                      <EnvironmentVariablesEditor
                        editor={mockedResponseEditor}
                        className="flex-1  bg-silver-lighter"
                      />
                    </div>
                  )}
                </div>
              }
            />
          </ModalBody>
          <ModalFooter className="border-t border-t-primary">
            <div className="flex items-center text-sm text-secondary">
              <Button
                variant="hidden"
                onClick={() => layoutInfo.setLeftPaneOpen((v) => !v)}
                leftIcon={
                  layoutInfo.leftPaneOpen ? (
                    <IconChevronLeft />
                  ) : (
                    <IconChevronRight />
                  )
                }
              >
                {layoutInfo.leftPaneOpen ? 'Hide' : 'Show'} snippets
              </Button>
            </div>
            <div className="mx-6 flex items-center text-sm text-secondary">
              <IconInfo className="mr-2" />
              <div>
                Shared operations and personal operations cannot be used in the
                same script.
              </div>
            </div>
            <ButtonGroup>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                data-testid="add_operation_script"
              >
                Add script
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {oauth2Request && (
        <PreflightScriptOAuthConfirmationModal
          {...oauth2Request}
          isOpen={!!oauth2Request}
        />
      )}
      {promptRequest && <ScriptPromptModal {...promptRequest} />}
    </>
  );
};
